import * as React from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import CoffeeIcon from '@mui/icons-material/Coffee';
import GppBadIcon from '@mui/icons-material/GppBad';
import EngineeringIcon from '@mui/icons-material/Engineering';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.3} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

AreaGradient.propTypes = {
  color: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

function StatCard({ title, value, agentsStates, trend, trend2, chip, data, dataParam, varian }) {
  const labelColors = {
    up: 'success',
    down: 'error',
    warning: 'warning',
    neutral: 'default',
    info: 'info',
  };

  const color = labelColors[trend];
  let color2=''
  if(trend2){
    color2 = labelColors[trend2];
  }

  return (
    <Card variant="outlined" sx={{ height: '100%', flex: 1 }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        <Stack
          direction="column"
          sx={{ justifyContent: 'space-between', flexGrow: '1', gap: 0.5 }}
        >
          <Stack sx={{ justifyContent: 'space-between' }}>
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="h5" component="p" style={{color: color2}}>
                {value}
              </Typography>
              <Chip size="small" color={color} label={chip} variant={varian}/>
            </Stack>
          </Stack>
          {agentsStates&&
                <span>
                    <Chip 
                        size="small" 
                        color="warning" 
                        variant="outlined" 
                        label={`Отдых: ${agentsStates.rest}`} 
                        style={{fontSize: '0.6rem', marginRight: 4}}
                        icon={<CoffeeIcon style={{marginRight: 2, fontSize: '0.9rem'}}/>}
                    />
                    <Chip 
                        size="small" 
                        color="error" 
                        variant="outlined" 
                        label={`Не готов: ${agentsStates.notReady}`} 
                        style={{fontSize: '0.6rem', marginRight: 4}}
                        icon={<HighlightOffIcon style={{marginRight: 2, fontSize: '0.9rem'}}/>}
                    />
                    <Chip 
                        size="small" 
                        color="info" 
                        variant="outlined" 
                        label={`Работаю: ${agentsStates.work}`} 
                        style={{fontSize: '0.6rem', marginRight: 4}}
                        icon={<EngineeringIcon style={{marginRight: 2, fontSize: '0.9rem'}}/>}
                    />
                    <Chip 
                        size="small" 
                        color="error" 
                        variant="outlined" 
                        label={`Другой: ${agentsStates.other}`} 
                        style={{fontSize: '0.6rem', marginRight: 4}}
                        icon={<GppBadIcon style={{marginRight: 2, fontSize: '0.9rem'}}/>}
                    />
                </span>
            }
            {data&&
              <Box className="pc-dash-chart">
                <ResponsiveContainer
                    width="99%"
                    height="100%"
                >
                    <AreaChart data={data} style={{fontSize: "12px", color: "#0b2b61"}} margin={{top: 2, right: 8, left: 0, bottom: 0}}>  
                        <defs>
                            <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#28589C" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#28589C" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorAbandoned" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#b82e2e" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#b82e2e" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorAnswered" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#22aa99" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#22aa99" stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <XAxis dataKey="name" hide={true}/>
                        {/* <YAxis/> */}
                        {/* <Legend/> */}
                        {/* <CartesianGrid strokeDasharray="3 3"/> */}
                        <Tooltip 
                          wrapperStyle={{ zIndex: 6, pointerEvents: 'none' }}
                          allowEscapeViewBox={{ x: true, y: true }} 
                          formatter={value => {
                            return dataParam==="sl" ? `${value.toFixed(2)}%` : value;
                          }} 
                        />
                        {dataParam==="answered"&&
                          <>
                            <Area name="Всего" type="monotone" dataKey="total" stroke="#28589C" fillOpacity={10} fill="url(#colorTotal)"/>
                            <Area name="Принятых" type="monotone" dataKey="answered" stroke="#22aa99" fillOpacity={10} fill="url(#colorAnswered)" />
                            <Area name="Потерянных" type="monotone" dataKey="abandoned" stroke="#b82e2e"fillOpacity={10} fill="url(#colorAbandoned)"/>
                          </>
                        }
                        {/* <Area name="Всего" type="monotone" dataKey="total" stroke="#28589C" fillOpacity={1} fill="url(#colorTotal)"/> */}
                        {dataParam==="sl"&&
                            <>
                                <Area name="SL" type="monotone" dataKey="sl" stroke="#22aa99" fillOpacity={1} fill="url(#colorAnswered)" />
                                <Area name="AR" type="monotone" dataKey="abandonedPercent" stroke="#b82e2e" fillOpacity={1} fill="url(#colorAbandoned)" />
                                {/* <Area name="Всего" type="monotone" dataKey="total" stroke="#28589C" fillOpacity={10} fill="url(#colorTotal)"/>
                                <Area name="Принятых" type="monotone" dataKey="answered" stroke="#22aa99" fillOpacity={10} fill="url(#colorAnswered)" />
                                <Area name="Потерянных" type="monotone" dataKey="abandoned" stroke="#b82e2e"fillOpacity={10} fill="url(#colorAbandoned)"/> */}
                            </>
                          }
                    </AreaChart>
                </ResponsiveContainer> 
              </Box>
            }
        </Stack>
      </CardContent>
    </Card>
  );
}

StatCard.propTypes = {
//   data: PropTypes.arrayOf(PropTypes.number).isRequired,
//   interval: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
//   trend: PropTypes.oneOf(['down', 'neutral', 'up']).isRequired,
  value: PropTypes.string.isRequired,
};

export default StatCard;